import { SOB_QUARTER_KEYS } from '../model/constants'
import type {
  SobInputKey,
  SobInputState,
  SobInputStateByKey,
  SobPlanningTableModel,
  SobQuarterKey,
  SobQuarterValues,
  SobSalesKey,
  SobTableEntry,
} from '../model/types'

const SALES_KEY_BY_INPUT_KEY: Record<SobInputKey, SobSalesKey> = {
  prevActuals: 'prevActualsSales',
  currentActuals: 'currentActualsSales',
  currentPlan: 'currentPlanSales',
}

export const isIncompleteSobValue = (value: number | null) => value == null || value === 0

export const createSobInputState = (model: SobPlanningTableModel): SobInputState => ({
  prevActuals: model.sob.prevActuals,
  currentActuals: model.sob.currentActuals,
  currentPlan: model.sob.currentPlan,
})

export const flattenSobTableEntries = (entries: SobTableEntry[]): SobTableEntry[] =>
  entries.flatMap((entry) => [
    entry,
    ...(entry.childEntries ? flattenSobTableEntries(entry.childEntries) : []),
  ])

export const getEditableSobTableEntries = (entries: SobTableEntry[]): SobTableEntry[] => {
  const subCategoryEntries = entries.filter((entry) => !entry.isOverall)

  if (!subCategoryEntries.length) {
    return entries
  }

  return subCategoryEntries.flatMap((entry) =>
    entry.childEntries?.length ? getEditableSobTableEntries(entry.childEntries) : [entry]
  )
}

export const buildInitialInputsByKey = (entries: SobTableEntry[]): SobInputStateByKey => {
  return flattenSobTableEntries(entries).reduce<SobInputStateByKey>((acc, entry) => {
    acc[entry.key] = createSobInputState(entry.model)

    return acc
  }, {})
}

export const buildNextInputsByKey = (
  entries: SobTableEntry[],
  currentInputsByKey: SobInputStateByKey
): SobInputStateByKey =>
  flattenSobTableEntries(entries).reduce<SobInputStateByKey>((acc, entry) => {
    const nextEntryInputs = createSobInputState(entry.model)
    const currentEntryInputs = currentInputsByKey[entry.key]

    acc[entry.key] = currentEntryInputs
      ? {
          ...nextEntryInputs,
          prevActuals: currentEntryInputs.prevActuals,
          currentActuals: currentEntryInputs.currentActuals,
        }
      : nextEntryInputs

    return acc
  }, {})

const isPositiveFiniteNumber = (value: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0

const getEntrySalesValue = (
  entry: SobTableEntry,
  salesKey: SobSalesKey,
  quarterKey: SobQuarterKey
) => {
  const salesValue = entry.model.sob[salesKey][quarterKey]

  if (isPositiveFiniteNumber(salesValue) || !entry.childEntries?.length) {
    return salesValue
  }

  const childSalesTotal = entry.childEntries.reduce((total, childEntry) => {
    const childSalesValue = childEntry.model.sob[salesKey][quarterKey]

    return isPositiveFiniteNumber(childSalesValue) ? total + childSalesValue : total
  }, 0)

  return childSalesTotal > 0 ? childSalesTotal : salesValue
}

const calculateOverallQuarterSob = ({
  sourceEntries,
  inputsByKey,
  inputKey,
  quarterKey,
}: {
  sourceEntries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  inputKey: SobInputKey
  quarterKey: SobQuarterKey
}): number | undefined => {
  const salesKey = SALES_KEY_BY_INPUT_KEY[inputKey]
  let valueTotal = 0
  let sobValueTotal = 0

  sourceEntries.forEach((entry) => {
    const entryInputs = inputsByKey[entry.key] ?? createSobInputState(entry.model)
    const salesValue = getEntrySalesValue(entry, salesKey, quarterKey)
    const sobValue = entryInputs[inputKey][quarterKey]

    if (
      isPositiveFiniteNumber(salesValue) &&
      typeof sobValue === 'number' &&
      Number.isFinite(sobValue) &&
      sobValue > 0
    ) {
      valueTotal += salesValue
      sobValueTotal += salesValue / sobValue
    }
  })

  return sobValueTotal > 0 ? valueTotal / sobValueTotal : undefined
}

const recalculateParentInputs = ({
  parentEntry,
  sourceEntries,
  inputsByKey,
  inputKey,
  quarterKeys = SOB_QUARTER_KEYS,
}: {
  parentEntry: SobTableEntry
  sourceEntries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  inputKey: SobInputKey
  quarterKeys?: readonly SobQuarterKey[]
}): SobInputStateByKey => {
  if (!sourceEntries.length) {
    return inputsByKey
  }

  const parentInputs = inputsByKey[parentEntry.key] ?? createSobInputState(parentEntry.model)

  const nextParentQuarterValues: SobQuarterValues = { ...parentInputs[inputKey] }
  let hasUpdate = false

  quarterKeys.forEach((quarterKey) => {
    const nextQuarterValue = calculateOverallQuarterSob({
      sourceEntries,
      inputsByKey,
      inputKey,
      quarterKey,
    })

    if (nextQuarterValue !== undefined) {
      nextParentQuarterValues[quarterKey] = nextQuarterValue
      hasUpdate = true
    }
  })

  if (!hasUpdate) {
    return inputsByKey
  }

  return {
    ...inputsByKey,
    [parentEntry.key]: {
      ...parentInputs,
      [inputKey]: nextParentQuarterValues,
    },
  }
}

const getCategoryTableEntries = (entries: SobTableEntry[], category: string) =>
  flattenSobTableEntries(entries).filter((entry) => entry.category === category)

export const recalculateCategoryOverallInputs = ({
  entries,
  inputsByKey,
  inputKey,
  category,
  quarterKeys = SOB_QUARTER_KEYS,
}: {
  entries: SobTableEntry[]
  inputsByKey: SobInputStateByKey
  inputKey: SobInputKey
  category: string
  quarterKeys?: readonly SobQuarterKey[]
}): SobInputStateByKey => {
  if (!quarterKeys.length) {
    return inputsByKey
  }

  const categoryEntries = getCategoryTableEntries(entries, category)
  const overallEntry = categoryEntries.find((entry) => entry.isOverall)
  const sourceEntries = categoryEntries.filter((entry) => !entry.isOverall)

  if (!overallEntry || !sourceEntries.length) {
    return inputsByKey
  }

  return recalculateParentInputs({
    parentEntry: overallEntry,
    sourceEntries,
    inputsByKey,
    inputKey,
    quarterKeys,
  })
}
